import React, { useState } from 'react';
import { Plus, Search, Package, Save, X, Edit2, Trash2, Tag, Archive } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuthToken } from '../../../lib/useAuthToken';
import { useItems, useCreateItem, useUpdateItem, useDeleteItem } from '../hooks/useInventory';

const UNITS = ['g', 'kg', 'ml', 'litros', 'unidades', 'cajas', 'porciones', 'libras'];

const ItemsList: React.FC = () => {
  const t = useAuthToken();
  const { data: pageData, isLoading } = useItems({ limit: 500 }); // Cargamos varios
  const createMut = useCreateItem();
  const updateMut = useUpdateItem();
  const deleteMut = useDeleteItem();

  const items = pageData?.data ?? [];

  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<any>(null);
  const [isNew, setIsNew] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<any>(null);

  // Form state
  const [form, setForm] = useState({
    sku: '', name: '', category: '', unit: 'unidades', min_stock: 0, cost: 0, notes: '',
    purchase_unit: '', conversion_factor: 1
  });

  const reset = () => {
    setForm({ sku: '', name: '', category: '', unit: 'unidades', min_stock: 0, cost: 0, notes: '', purchase_unit: '', conversion_factor: 1 });
    setEditing(null);
    setIsNew(false);
  };

  const handleEdit = (i: any) => {
    setEditing(i);
    setForm({
      sku: i.sku || '',
      name: i.name || '',
      category: i.category || '',
      unit: i.unit || 'unidades',
      min_stock: i.min_stock || 0,
      cost: i.cost || 0,
      notes: i.notes || '',
      purchase_unit: i.purchase_unit || '',
      conversion_factor: i.conversion_factor !== undefined ? Number(i.conversion_factor) : 1
    });
    setIsNew(false);
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('El nombre es obligatorio');
    if (!form.unit) return toast.error('La unidad de medida es obligatoria');
    
    // Preparar payload
    const payload = {
      ...form,
      min_stock: Number(form.min_stock) || 0,
      cost: Number(form.cost) || 0,
      conversion_factor: Number(form.conversion_factor) || 1
    };

    // Si sku está vacío, enviarlo undefined o sin sku para que el backend lo auto genere
    if (!payload.sku) {
       delete (payload as any).sku;
    }

    try {
      if (isNew) {
        await createMut.mutateAsync(payload);
        toast.success('Insumo creado');
      } else {
        await updateMut.mutateAsync({ id: editing.id, data: payload });
        toast.success('Insumo actualizado');
      }
      reset();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Error al guardar el insumo');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    try {
      await deleteMut.mutateAsync(confirmDelete.id);
      toast.success('Insumo eliminado');
      setConfirmDelete(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Error al eliminar');
    }
  };

  const filtered = items.filter((i: any) =>
    i.name.toLowerCase().includes(q.toLowerCase()) ||
    (i.sku?.toLowerCase()?.includes(q.toLowerCase())) ||
    (i.category?.toLowerCase()?.includes(q.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-5xl space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted">Inventario</p>
            <h1 className="text-3xl font-bold text-[color:var(--color-text)] flex items-center gap-2">
              <Package size={28} className="text-primary" /> Ítems / Insumos
            </h1>
            <p className="mt-1 text-sm text-muted">Gestiona el catálogo de insumos e ingredientes del restaurante</p>
          </div>
          <button
            onClick={() => { reset(); setIsNew(true); }}
            className="inline-flex h-11 items-center gap-2 rounded-full bg-primary px-6 text-sm font-bold text-white shadow hover:opacity-90 transition"
          >
            <Plus size={18} /> Nuevo Insumo
          </button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Buscar por nombre, SKU o categoría..."
            className="w-full rounded-2xl border border-divider bg-surface px-11 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
          />
        </div>

        {/* Create / Edit Form */}
        {(isNew || editing) && (
          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[color:var(--color-text)]">
                {isNew ? 'Crear Nuevo Insumo' : `Editando: ${editing.name}`}
              </h2>
              <button onClick={reset} className="text-muted hover:text-red-500 transition"><X size={20}/></button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-muted">Nombre *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej: Leche deslactosada"
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-muted">SKU / Código {isNew ? '(opcional)' : ''}</label>
                <input
                  type="text"
                  value={form.sku}
                  onChange={e => setForm({ ...form, sku: e.target.value })}
                  placeholder="Autogenerado si está vacío"
                  disabled={!isNew} // SKU cannot be changed after creation in backend
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-muted">Categoría</label>
                <input
                  type="text"
                  value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                  placeholder="Ej: Lácteos"
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-muted">Unidad de Medida *</label>
                <select
                  value={form.unit}
                  onChange={e => setForm({ ...form, unit: e.target.value })}
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-muted">Stock Mínimo (Alerta)</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={form.min_stock}
                  onChange={e => setForm({ ...form, min_stock: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-muted">Costo Unitario ($) *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.cost}
                  onChange={e => setForm({ ...form, cost: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Por unidad/gramo"
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-1">
                <label className="mb-1 block text-xs font-semibold text-muted">Notas Adicionales</label>
                <input
                  type="text"
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-muted">Unidad de Compra (Opcional)</label>
                <input
                  type="text"
                  value={form.purchase_unit}
                  onChange={e => setForm({ ...form, purchase_unit: e.target.value })}
                  placeholder="Ej: Bidón, Paquete"
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-muted">Factor de Conversión</label>
                <input
                  type="number"
                  min="0.0001"
                  step="any"
                  value={form.conversion_factor}
                  onChange={e => setForm({ ...form, conversion_factor: parseFloat(e.target.value) || 1 })}
                  placeholder="Ej: 18000 o 5"
                  className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <span className="text-[10px] text-muted block mt-0.5">
                  1 {form.purchase_unit || 'unidad de compra'} = {form.conversion_factor} {form.unit}
                </span>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={reset} className="px-5 py-2 text-sm font-semibold text-muted hover:text-[color:var(--color-text)]">Cancelar</button>
              <button
                onClick={handleSave}
                disabled={createMut.isPending || updateMut.isPending}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-2 text-sm font-bold text-white shadow-lg hover:opacity-90 disabled:opacity-60 transition"
              >
                <Save size={16} /> {createMut.isPending || updateMut.isPending ? 'Guardando...' : 'Guardar Insumo'}
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {confirmDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="mx-4 w-full max-w-sm rounded-2xl bg-surface p-6 shadow-2xl border border-divider">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <Trash2 size={22} className="text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-[color:var(--color-text)]">Eliminar insumo</h3>
              <p className="mt-2 text-sm text-muted">
                ¿Estás seguro que deseas eliminar <span className="font-semibold text-[color:var(--color-text)]">{confirmDelete.name}</span>? Esta acción lo removerá y afectará cálculos de recetas asociadas, pero mantendrá histórico.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="px-5 py-2 text-sm font-semibold text-muted hover:text-[color:var(--color-text)] transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleteMut.isPending}
                  className="inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60 transition"
                >
                  <Trash2 size={14} /> {deleteMut.isPending ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* List */}
        {isLoading ? (
           <div className="p-8 text-center text-muted">Cargando catálogo...</div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((item: any) => (
              <div key={item.id} className="group relative flex flex-col justify-between rounded-2xl border border-divider bg-surface p-5 shadow-sm hover:shadow-md transition-all duration-300">
                {/* Action buttons */}
                <div className="absolute right-4 top-4 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition">
                  <button
                    onClick={() => handleEdit(item)}
                    className="rounded-full bg-muted p-2 text-muted hover:text-primary hover:bg-primary/10 transition"
                    title="Editar"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(item)}
                    className="rounded-full bg-muted p-2 text-muted hover:text-red-600 hover:bg-red-50 transition"
                    title="Eliminar"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                <div>
                  <div className="mb-2 max-w-[calc(100%-60px)]">
                    <h3 className="text-base font-bold text-[color:var(--color-text)] leading-tight">{item.name}</h3>
                    <div className="mt-1 flex items-center gap-2">
                       <p className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-md">{item.sku}</p>
                       {item.category && <p className="text-[10px] text-muted flex items-center gap-1"><Tag size={10} />{item.category}</p>}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
                    <div className="rounded-xl border border-divider p-2 bg-surface-muted">
                      <p className="text-[10px] font-bold uppercase text-muted mb-0.5 flex flex-wrap"><Archive size={12} className="mr-1 inline-block"/> Stock Disp.</p>
                      <p className={`font-semibold ${item.current_stock <= (item.min_stock || 0) ? 'text-red-500' : 'text-[color:var(--color-text)]'}`}>
                         {item.current_stock ? Number(item.current_stock).toFixed(2) : 0} <span className="text-xs text-muted font-normal">{item.unit}</span>
                      </p>
                    </div>
                    <div className="rounded-xl border border-divider p-2 bg-surface-muted">
                      <p className="text-[10px] font-bold uppercase text-muted mb-0.5">Mínimo</p>
                      <p className="font-semibold text-[color:var(--color-text)]">
                         {item.min_stock || 0} <span className="text-xs text-muted font-normal">{item.unit}</span>
                      </p>
                    </div>
                    <div className="rounded-xl border border-divider p-2 bg-surface-muted">
                      <p className="text-[10px] font-bold uppercase text-muted mb-0.5">Costo Unit.</p>
                      <p className="font-semibold text-emerald-600 dark:text-emerald-400">
                         ${Number(item.cost || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filtered.length === 0 && (
              <div className="col-span-full rounded-2xl border border-dashed border-divider p-12 text-center">
                <Package size={48} className="mx-auto mb-4 text-muted opacity-20" />
                <p className="text-muted text-lg">No se encontraron insumos</p>
                <p className="text-xs text-muted opacity-70 mb-4 mt-1">El catálogo de ingredientes y materiales está vacío para esta búsqueda.</p>
                <button onClick={() => { reset(); setIsNew(true); }} className="inline-flex h-10 items-center justify-center rounded-full bg-primary/10 px-5 text-sm font-bold text-primary hover:bg-primary/20 transition">Añadir el primero</button>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default ItemsList;
